import { makeAutoObservable, runInAction } from 'mobx'
import moment, { Moment } from 'moment'
import Invoice from 'src/models/Invoice'
import apiClient from 'src/utils/apiClient'

///////////////////////////////////
// Types
///////////////////////////////////

const CACHE_SIZE = 10

type GetInvoiceParams = {
  userId: number
  date: Moment
}

///////////////////////////////////
// Store
///////////////////////////////////

export default class InvoiceStore {
  private invoiceCaches = new Map<string, Invoice>()
  private billingAmountGuideCaches = new Map<string, number | null>()

  invoice: Invoice | null = null
  invoiceDates: Moment[] = []

  constructor() {
    makeAutoObservable(this)
  }

  // Setter ------------------------------------------------------

  setInvoice = (invoice: Invoice | null) => {
    this.invoice = invoice
  }

  // Getter ------------------------------------------------------

  getBillingAmountGuide = async ({ userId, date }: GetInvoiceParams) => {
    const cacheKey = this.cacheKey(date)
    const cache = this.billingAmountGuideCaches.get(cacheKey)
    if (cache !== void 0) return cache

    const { billingAmountGuide } = await apiClient.invoices.getBillingAmountGuide({
      userId,
      year: date.year(),
      month: date.month() + 1,
    })

    this.billingAmountGuideCaches.set(cacheKey, billingAmountGuide)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.billingAmountGuideCaches.size > CACHE_SIZE)
      this.billingAmountGuideCaches.delete(this.billingAmountGuideCaches.keys().next().value)

    return billingAmountGuide
  }

  getInvoice = async ({ userId, date }: GetInvoiceParams) => {
    const cacheKey = this.cacheKey(date)
    const cache = this.invoiceCaches.get(cacheKey)
    if (cache) return this.setInvoice(cache)

    const invoiceData = await apiClient.invoices.getInvoice({
      userId,
      year: date.year(),
      month: date.month() + 1,
    })

    const invoice = new Invoice(invoiceData)

    this.invoiceCaches.set(cacheKey, invoice)

    // キャッシュサイズを超えた場合、最も古いキャッシュを削除
    if (this.invoiceCaches.size > CACHE_SIZE) this.invoiceCaches.delete(this.invoiceCaches.keys().next().value)

    this.setInvoice(invoice)
  }

  getInvoiceDates = async (userId: number) => {
    const dates = await apiClient.invoices.getInvoiceDates({ userId })

    runInAction(() => {
      this.invoiceDates = dates.map((date) => moment(date))
    })
  }

  // Private actions ---------------

  private cacheKey = (date: Moment) => {
    return date.format('YYYY-MM')
  }
}
